import { memo } from "react";
import { Box, Typography } from "@mui/material";
import MailOutlineRounded from "@mui/icons-material/MailOutlineRounded";
import MessageForm, { type MessageFormProps } from "./MessageForm";

type Props = MessageFormProps & {
  title: string;
  intro?: React.ReactNode;
  /** Left-aligned card (default) or centred under a centred page. */
  align?: "left" | "center";
  "data-testid"?: string;
};

/**
 * A titled "send us a message" card for pages that would otherwise be a dead
 * end: 404, an error screen, a home we can't find, a section not built yet.
 */
const MessageUsPanel = memo(({ title, intro, align = "left", "data-testid": testId, ...formProps }: Props) => (
  <Box
    data-testid={testId || "message-us-panel"}
    sx={{
      width: "100%",
      maxWidth: 680,
      mx: align === "center" ? "auto" : 0,
      textAlign: "left",
      backgroundColor: "#f6f9f9",
      border: "1px solid #e3eded",
      borderRadius: "16px",
      p: { xs: 2.5, md: 4 },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: intro ? 1 : 2.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          backgroundColor: "rgba(0,75,76,0.1)",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        <MailOutlineRounded sx={{ color: "primary.main", fontSize: 22 }} />
      </Box>
      <Typography variant="h6" component="h2" fontWeight={700} color="primary.main">
        {title}
      </Typography>
    </Box>
    {intro && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        {intro}
      </Typography>
    )}
    <MessageForm onTint {...formProps} />
  </Box>
));

export default MessageUsPanel;
